import { Grid, TextField } from "@mui/material";
import { Documento, DynFormProps } from "..";
import { Controller, useFormContext } from "react-hook-form";
import SelectHistoricoFinanceiro from "../../SelectsSimpleApiQuery/SelectHistoricoFinanceiro";
import SelectPlanoContas from "../../SelectsSimpleApiQuery/SelectPlanoContas";
import SelectNaturezaRendimento from "../../SelectsSimpleApiQuery/SelectNaturezaRendimento";
import BasicDatePicker from "../../BasicDatePicker";
import moment, { Moment } from "moment";
import { useEffect } from "react";
import { useMutation } from "react-query";
import axiosClient from "../../../lib/axios";

export default function DynInfoContabeis({ spec, extras }: DynInfoContabeisProps) {
    const { control, setValue, getValues } = useFormContext();

    const dataEmissao = getValues(`${spec.name}.data_emissao`);
    const dataVencimento = getValues(`${spec.name}.data_vencimento`);

    const { mutateAsync: validacaoData, isLoading: isLoadingValidacaoData } = useMutation({
        mutationKey: 'validacao-data',
        mutationFn: (inputs: Moment) => axiosClient.post(`/validacao-data`, inputs, {
            params: {
                data: inputs?.format("DD-MM-YYYY"),
            },
        }),
        onError: (error) => {
            console.error("Erro na validação da data:", error);
        }
    });

    useEffect(() => {
        if (extras?.data_emissao && !dataEmissao) {
            setValue(`${spec.name}.data_emissao`, moment(extras.data_emissao, "YYYY-MM-DD"));
        }
        if (extras?.data_vencimento && !dataVencimento) {
            setValue(`${spec.name}.data_vencimento`, moment(extras.data_vencimento, "YYYY-MM-DD"));
        }
    }, [extras, spec, dataEmissao, dataVencimento]);

    return (
        <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 12, md: 6 }}>
                <Controller
                    name={`${spec.name}.data_emissao`}
                    control={control}
                    rules={{
                        required: "Campo obrigatório",
                        validate: async (value) => {
                            if (!value) return true;

                            const dataVencimento = getValues(`${spec.name}.data_vencimento`);
                            if (dataVencimento) {
                                const dataVencimentoMoment = moment(dataVencimento, "YYYY-MM-DD");
                                const dataEmissaoMoment = moment(value, "YYYY-MM-DD");
                                if (dataEmissaoMoment.isAfter(dataVencimentoMoment)) {
                                    return "Data de emissão não pode ser maior que a data de vencimento";
                                }
                            }

                            try {
                                const dataMoment = moment(value, "YYYY-MM-DD");
                                if (!dataMoment.isValid()) {
                                    return "Data inválida";
                                }

                                const response = await validacaoData(dataMoment);

                                if (response?.data === false) {
                                    return response?.data?.message || "Data inválida";
                                }

                                return true;
                            } catch (error: any) {
                                console.error("Erro ao validar a data:", error);

                                if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
                                    return "Erro de conexão. Verifique sua internet.";
                                }

                                return error?.response?.data?.message || "Erro ao validar a data";
                            }
                        }
                    }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <BasicDatePicker
                            label="Emissão"
                            required
                            helperText={error ? error.message : ""}
                            error={invalid}
                            disabled={isLoadingValidacaoData}
                            value={
                                field.value
                                    ? moment(field.value, "YYYY-MM-DD")
                                    : moment(extras?.data_emissao, "YYYY-MM-DD")
                            }
                            onChange={(data) => {
                                if (data && moment(data).isValid()) {
                                    field.onChange(moment(data).format("YYYY-MM-DD"));
                                } else if (data === null) {
                                    field.onChange(null);
                                }
                            }}
                            formatString="DD/MM/YYYY"
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.historico`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error, invalid } }) => {
                        return (
                            <SelectHistoricoFinanceiro
                                {...field}
                                required
                                error={invalid}
                                value={field.value}
                                onChange={(valor) => {
                                    field.onChange(valor);
                                    setValue(`${spec.name}.historico.nome`, valor?.nome);
                                    setValue(`${spec.name}.historico.descricao`, valor?.nome);
                                }}
                            />
                        );
                    }}
                />
            </Grid>

            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.historico.descricao`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            label="Descrição histórico"
                            value={field.value || ""}
                            fullWidth
                            required
                            error={Boolean(error)}
                            helperText={error ? "Campo obrigatório" : ""}
                            onChange={(e) => {
                                setValue(`${spec.name}.historico.descricao`, e.target.value);
                            }}
                            slotProps={{ htmlInput: { maxLength: 60 } }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.conta_devedora`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <SelectPlanoContas
                            inputLabel="Conta devedora"
                            value={field.value}
                            required
                            error={invalid}
                            onChangeValue={(valor) => field.onChange(valor)}
                            extraParams={{ tipo: "Analítica" }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.conta_credora`}
                    control={control}
                    rules={{ required: "Campo obrigatório" }}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <SelectPlanoContas
                            inputLabel="Conta credora"
                            required
                            error={invalid}
                            value={field.value}
                            onChangeValue={(valor) => field.onChange(valor)}
                            extraParams={{ sistema_xy: "D-PAG", tipo: "Analítica" }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 6 }}>
                <Controller
                    name={`${spec.name}.natureza_rendimento`}
                    control={control}
                    render={({ field, fieldState: { error, invalid } }) => (
                        <SelectNaturezaRendimento
                            value={field.value}
                            onChange={(valor) => {
                                field.onChange(valor);
                                setValue(`${spec.name}.natureza_rendimento`, valor);
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Controller
                    name={`${spec.name}.comentario`}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            label="Comentário"
                            value={field.value || ""}
                            fullWidth
                            onChange={(e) => {
                                setValue(`${spec.name}.comentario`, e.target.value);
                            }}
                            slotProps={{ htmlInput: { maxLength: 70 } }}
                        />
                    )}
                />
            </Grid>
        </Grid>
    );
}

interface DynInfoContabeisProps extends DynFormProps {
    extras: Documento | null;
}
